import ResultLogo from "@components/job_search/search_result_card/result_logo.tsx";
import TitleCompany from "@components/job_search/search_result_card/title_company.tsx";
import Tags from "@components/job_search/search_result_card/tags.tsx";
import Footer from "@components/job_search/search_result_card/footer.tsx";
import { Link, SecondaryBody } from "@sprout/typography.tsx";
import { IconButton } from "@sprout/icon_button.tsx";
import BookmarkIcon from "@sprout/icons/bookmark.tsx";
import ReminderIcon from "@sprout/icons/reminder.tsx";
import Tooltip from "@sprout/tooltip.tsx";
import { type JobDescriptionSummary, joinSummary } from "@/utils/job_description_summary.ts";
import { cn } from "@/lib/utils";
import useTranslation from "@hooks/use_translation";
import { type MouseEvent, useEffect, useRef } from "react";

interface Props {
  id: string;
  title: string;
  company: string;
  logoUrl: string;
  locations?: string[];
  workType?: string | null;
  payRange?: string;
  publishedAt: string;
  url: string;
  external?: boolean;
  loading?: boolean;
  trackClick?: () => void;
  viewed: boolean;
  onView: (id: string) => void;
  appliedAt: string | null;
  onCreateAlert?: () => void;
  onSave?: () => void;
  onSelect?: () => void;
  summary?: JobDescriptionSummary | null;
  description?: string | null;
  selected?: boolean;
}

export const SearchResultCard = ({
  id,
  title,
  company,
  logoUrl,
  locations,
  workType,
  payRange,
  publishedAt,
  url,
  external,
  loading,
  trackClick,
  viewed,
  onView,
  appliedAt,
  onCreateAlert,
  onSave,
  onSelect,
  summary,
  description,
  selected,
}: Props) => {
  const { t } = useTranslation("jobSearch");
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selected && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [selected]);
  const isExternal = external ?? true;
  const text = joinSummary(summary) ?? description;
  const handleCardClick = (e: MouseEvent) => {
    if (loading) {
      e.preventDefault();
      return;
    }
    trackClick?.();
    if (!viewed) {
      onView(id);
    }
    if (!isExternal) {
      e.preventDefault();
      onSelect?.();
    }
  };

  return (
    <div
      ref={cardRef}
      data-provides="search-result"
      data-loading={loading ? "true" : "false"}
      data-selected={selected ? "true" : undefined}
      className={cn(
        "relative border rounded-lg border-solid hover:bg-[var(--pale-blue)]",
        viewed ? "bg-[var(--object-fills-grey)]" : "bg-white",
        selected
          ? "border-[var(--green-700)] ring-1 ring-[var(--green-700)]"
          : "border-[var(--gray-700)]",
      )}
    >
      <Link
        href={url}
        external={isExternal}
        onClick={handleCardClick}
        className="flex flex-col h-full px-6 py-4 gap-4 justify-between no-underline! text-inherit! hover:brightness-100! focus:brightness-100!"
      >
        <div
          className={`flex flex-row items-center gap-2 ${onSave || onCreateAlert ? "pr-11" : ""}`}
        >
          <ResultLogo loading={loading} company={company} logoUrl={logoUrl} />
          <div className="flex flex-col min-w-0">
            <TitleCompany loading={loading} company={company} title={title} />
          </div>
        </div>
        <Tags
          loading={loading}
          locations={locations}
          workType={workType}
          payRange={payRange}
          showDescription={!!text}
        />
        {!loading && text && <SecondaryBody className="line-clamp-4">{text}</SecondaryBody>}
        <div className="flex flex-row items-center justify-between">
          <Footer
            loading={loading}
            publishedAt={publishedAt}
            viewed={viewed}
            appliedAt={appliedAt}
          />
        </div>
      </Link>
      {onSave ? (
        <div className="absolute right-6 top-5">
          <Tooltip text={t("saveJob")} asChild>
            <IconButton
              icon={BookmarkIcon}
              label={t("saveJob")}
              size="md"
              onClick={onSave}
              data-provides="save-job"
            />
          </Tooltip>
        </div>
      ) : (
        onCreateAlert && (
          <div className="absolute right-6 top-5">
            <Tooltip text={t("createJobAlert")} asChild>
              <IconButton
                icon={ReminderIcon}
                label={t("createJobAlert")}
                size="md"
                onClick={onCreateAlert}
                data-provides="create-job-alert"
              />
            </Tooltip>
          </div>
        )
      )}
    </div>
  );
};
